
import { Vue, Options, prop } from "vue-class-component";
import { parseZodError, validationCurrentIndex } from "../../utils";
import {
  sttReferenceSchema,
  sttReferenceOptionalSchema,
  noSttSchema
} from "./validationSttManualReference";
import {
  ModelStt,
  ErrorBookingV3,
  ConfigValidationForm,
  BookingV3
} from "@/domain/entities/CreateBookingV3";
import { debounce } from "lodash";
import { BookingController } from "@/app/ui/controllers/BookingController";
import { BulkBookingController } from "@/app/ui/controllers/BulkBookingController";
import { AccountController } from "@/app/ui/controllers/AccountController";
import {
  ShipmentBooking,
  SttAdjustmentRule
} from "@/domain/entities/ShipmentBooking";
import { ShipmentBookingController } from "@/app/ui/controllers/ShipmentBookingController";
import { RequestValidateShipmentId } from "@/data/payload/api/ShipmentBookingRequest";
import { FlagsPermissionBooking } from "@/feature-flags/flag-booking";

class Props {
  modelValue = prop<any>({
    required: true
  });
  id = prop<string>({
    default: "",
    type: String
  });
  isDO = prop<boolean>({
    default: false,
    type: Boolean
  });
  sttNoRefExternalIndex = prop<number>({
    default: 0,
    type: Number
  });
  currentIndex = prop<number>({
    default: 0,
    type: Number
  });
  isSttAdjustRetail = prop<boolean>({
    default: false,
    type: Boolean
  });
  isSttAdjustClient = prop<boolean>({
    default: false,
    type: Boolean
  });
  error = prop<ErrorBookingV3>({
    required: true
  });
  configManualStt = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  configRefExternal = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  errorSttReference = prop<any>({
    required: true,
    default: false
  });
  errorSttManual = prop<any>({
    required: true,
    default: false
  });
  errorShipmentId = prop<any>({
    required: false,
    default: false
  });
  formType = prop<any>({
    default: "",
    type: String
  });
  loadingValidateShipmentId = prop<boolean>({
    default: false,
    type: Boolean
  });
}

@Options({
  emits: ["update:modelValue", "update:currentIndex", "retry"],
  components: {},
  watch: {
    isClientAndDo() {
      // Reset validation state when isClientAndDo changes
      this.errorReferenceNumber = "";
      this.errorReference = false;
      this.isErrorSttReference = false;

      // Only validate if there's a value
      if (this.modelValue.referenceExternal) {
        this.validateRefNumber(
          this.modelValue.referenceExternal,
          this.requiredRefExternal
        );
      }
    },
    errorReferenceNumber(message: string) {
      this.errorReferenceNumberModel = message;
    },
    errorSttMessage(message: string) {
      this.errorSttManualModel = message;
    },

    currentIndex(index: number) {
      const isRequiredField = this.isClientAndDo && !this.requiredRefExternal;
      const schemaToUse = isRequiredField
        ? sttReferenceSchema
        : sttReferenceOptionalSchema;

      const value = this.modelValue.referenceExternal;
      const error = parseZodError(schemaToUse, value);

      if (error && validationCurrentIndex(this.sttNoRefExternalIndex, index)) {
        this.errorReferenceNumber = error;
        this.errorReference = true;
        this.isErrorSttReference = true;
      }
    },
    "modelValue.clientName.clientCode": {
      handler() {
        this.validateShipmentId(this.modelValue.shipmentId, this.modelValue);
      },
      immediate: true,
      deep: true
    }
  }
})
export default class SttManualReference extends Vue.with(Props) {
  errorReferenceNumber = "";
  errorReference = false;
  loadingSttRefExtCheck = false;
  isSuccessCheckStt = false;
  errorSystem = false;
  clientIdFromShipmentId = 0;

  validateRefNumber = debounce(
    async (
      value: string,
      isClientAndDo: boolean,
      requiredRefExternal: boolean
    ) => {
      this.updateCurrentIndex();
      this.isSuccessCheckStt = false;
      this.errorSystem = false;

      const isRequiredField = isClientAndDo && !requiredRefExternal;

      const schemaToUse = isRequiredField
        ? sttReferenceSchema
        : sttReferenceOptionalSchema;

      const error = parseZodError(schemaToUse, value);

      if (error) {
        this.errorReferenceNumber = error;
        this.errorReference = true;
        this.isErrorSttReference = true;
        return;
      }

      if (!value) {
        this.errorReference = false;
        this.errorReferenceNumber = "";
        this.isErrorSttReference = false;
        return;
      }

      this.loadingSttRefExtCheck = true;
      await BookingController.validateSttReferenceExt({
        validateType: "no_ref_external",
        noRefExternal: value
      })
        .then((res) => {
          this.errorReference = !res.isAllowBooking;
          this.errorReferenceNumber = res.errorMessage;
          this.isSuccessCheckStt = !res.errorMessage && res.isAllowBooking;
          this.isErrorSttReference = this.errorReference;
        })
        .catch(() => {
          this.errorSystem = true;
        });
      this.loadingSttRefExtCheck = false;
    },
    250
  );

  errorSttMessage = "";
  errorStt = false;
  validateSttManual = debounce(async (value: string, modelValue: any) => {
    if (value === "") {
      this.errorSttMessage = "";
      this.errorStt = false;
      this.isErrorSttManual = false;
      return;
    } else {
      const result = await BookingController.validateSttManual({
        clientId: this.getValidateSttManual(modelValue),
        sttManual: value,
        bookingType:
          this.formType === "manual" &&
          this.getAccount.accountType === "partner"
            ? "pos"
            : "client",
        productType: modelValue.productName
      });
      this.errorStt = !result.isAllowBooking;
      this.errorSttMessage = this.getAccount.accountIsForeign
        ? result.errorMessageEn || result.errorMessage
        : result.errorMessage;
      this.isErrorSttManual = this.errorStt;
    }
  }, 250);

  errorShipmentIdMessage = "";
  validateShipmentId = debounce(
    async (value: string, modelValue: BookingV3) => {
      try {
        this.errorShipmentIdMessage = "";
        this.$emit("update:errorShipmentId", false);
        this.$emit("update:loadingValidateShipmentId", false);
        if (!value) return;
        this.$emit("update:loadingValidateShipmentId", true);
        const result = await ShipmentBookingController.validateShipmentIdWithResponse(
          new RequestValidateShipmentId({
            clientCode: modelValue.clientName.clientCode || "",
            shipmentId: value
          })
        );
        // prevent infinite looping
        if (
          this.flagEnableHandleSelectClientIdFromShipment &&
          result &&
          modelValue.shipmentId === value &&
          modelValue.clientName.clientId !== result?.clientId
        ) {
          this.clientIdFromShipmentId = result.clientId;
          this.$emit("handleGetClientData", result);
        }
      } catch (error) {
        this.$emit("update:errorShipmentId", true);
        const { id, en } = error.response.data.message;
        this.errorShipmentIdMessage = this.getAccount.accountIsForeign
          ? en
          : id;
      } finally {
        this.$emit("update:loadingValidateShipmentId", false);
      }
    },
    250
  );

  mounted() {
    window.addEventListener("success-booking", this.resetForm);
  }

  unmounted() {
    window.removeEventListener("success-booking", this.resetForm);
  }

  get disabledSttManual() {
    return this.configManualStt.rule.includes(
      ConfigValidationForm.NON_EDITABLE
    );
  }

  get requiredSttManual() {
    return this.configManualStt.isRequired;
  }

  get disabledRefExternal() {
    return this.configRefExternal.rule.includes(
      ConfigValidationForm.NON_EDITABLE
    );
  }

  get requiredRefExternal() {
    return this.configRefExternal.isRequired;
  }

  get errorReferenceNumberModel() {
    return this.error.referenceNumber;
  }

  set errorReferenceNumberModel(message: string) {
    this.$emit("update:error.referenceNumber", message);
  }

  get errorSttManualModel() {
    return this.error.sttManual;
  }

  set errorSttManualModel(message: string) {
    this.$emit("update:error.manualStt", message);
  }

  get isErrorSttReference() {
    return this.errorSttReference;
  }

  set isErrorSttReference(value: any) {
    this.$emit("update:errorSttReference", value);
  }

  get isErrorSttManual() {
    return this.errorSttReference;
  }

  set isErrorSttManual(value: any) {
    this.$emit("update:errorSttManual", value);
  }

  getValidateSttManual(modelValue: any) {
    if (this.getAccount.accountType === "client") {
      return this.getAccount.accountClientId;
    } else if (this.formType === "client") {
      if (
        this.flagEnableHandleSelectClientIdFromShipment &&
        !modelValue.clientName.id &&
        this.clientIdFromShipmentId
      ) {
        return this.clientIdFromShipmentId;
      }
      return modelValue.clientName.id;
    } else {
      return "";
    }
  }

  get getAccount(): any {
    return {
      accountType: AccountController.accountData.account_type,
      accountClientId: AccountController.accountData.account_type_detail.id
        ? AccountController.accountData.account_type_detail.id
        : "",
      accountIsForeign: AccountController.accountData.accountIsForeign
    };
  }

  getErrorMandatory(value: string) {
    return !!parseZodError(sttReferenceSchema, value);
  }

  getErrorMessage(value: string) {
    return parseZodError(sttReferenceSchema, value);
  }

  getErrorStt(value: string) {
    return !!parseZodError(noSttSchema, value);
  }

  getErrorMessageStt(value: string) {
    return parseZodError(noSttSchema, value);
  }

  updateCurrentIndex() {
    if (
      !validationCurrentIndex(this.sttNoRefExternalIndex, this.currentIndex)
    ) {
      this.$emit("update:currentIndex", this.sttNoRefExternalIndex);
    }
  }

  get isClientAndDo() {
    return this.isDO;
  }

  resetForm() {
    this.isSuccessCheckStt = false;
  }

  onKeyDownShipmentId(event: any) {
    if (event.key === "Tab") return;
    this.$emit("update:loadingValidateShipmentId", true);
  }

  get flagEnableHandleSelectClientIdFromShipment() {
    return (
      AccountController.accountData.account_type_detail.type === "pos" &&
      FlagsPermissionBooking.permission_generate_client_data_from_shipment_id.isEnabled()
    );
  }
}
